































































































































































/* General */
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import moment from 'moment'

/* Mixins */
import EntityLandingMixin from '@/mixins/entity-landing.mixin'

/* Components */
import MailboxHomeHeaderOptions from './components/mailbox-home-header-options.vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'
import TextAssistant from '@/helpers/text-assistant'
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities, SortOptionValues } from '@/types'
import {
  IBreadcrumbItemMenuItem,
  IHomeTableItem,
} from '@/types/data-objects/items'
import { IItemTraitSet } from '@/types/data-objects/listing'
import {
  IConvosOutputSearchConvo,
  IMailboxesOutputFilterOptionsForListing,
} from '@/api/types/response.types'
import {
  IMailboxesInputManageCustomFilter,
  IMailboxesInputUpdateCustomFilter,
} from '@/api/types/request.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import ConversationsAPI from '@/api/modules/convos.api'
import CustomFiltersAPI from '@/api/modules/custom-filters.api'

export default EntityLandingMixin.extend({
  name: 'MailboxHome',

  components: {
    ItemsListingLayout,
    MailboxHomeHeaderOptions,
  },

  data() {
    return {
      ColorAssistant,
      // For entity-landing mixin
      isMailbox: true,
      showMergeConvosModal: false,
    }
  },

  beforeDestroy() {
    this.endPolling()
  },

  async created() {
    this.isLoadingInitialData = true
    await Promise.all([
      this.fetchDetails(),
      this.fetchFilterOptionsForListing(),
    ])
    this.isLoadingInitialData = false
    LocalStore.lastItemListRouteVisited = RouteAssistant.getMailboxRoute(
      this.accountID,
      this.$route.params.id,
      this.name,
    )
    this.startPolling()
    this.postCreated()
    this.fetchDefaultFilterOptionCounts()
    await this.showInfoOnEntityLandingIfNecessary()
  },

  methods: {
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async reloadData() {
      await Promise.all([
        this.fetchFilterOptionsForListing(),
        this.fetchResults(false),
      ])
      this.fetchDefaultFilterOptionCounts()
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await MailboxesAPI.get_LastUpdatedEpochTime(
        this.id,
      )
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await Promise.all([
          this.fetchDetails(),
          this.fetchFilterOptionsForListing(),
          this.fetchResults(false),
        ])
        this.fetchDefaultFilterOptionCounts()
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async fetchDetails() {
      try {
        const details = await MailboxesAPI.get_Detail(this.id)
        this.name = details.name
        this.members = details.listPeople
        this.workflow = details.workflow
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async fetchResults(showResultsLoading: boolean = true) {
      this.selectedItemIDs = []
      this.isLoadingResults = showResultsLoading
      const params = {
        page: this.currentPage,
        priority_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.priorityIDs,
        ),
        status_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.statusIDs,
        ),
        status_type: this.itemTraitSet.statusType,
        assignee_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.assigneeIDs,
        ),
        assigned_not_to_you: this.itemTraitSet.assignedNotToYou,
        job_appointee_groupings_string: this.itemTraitSet
          .jobAppointeeGroupingsString,
        including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.includingTagIDs,
        ),
        not_including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.notIncludingTagIDs,
        ),
        start_due_date: this.itemTraitSet.startDueDate,
        end_due_date: this.itemTraitSet.endDueDate,
        due_type: this.itemTraitSet.dueType,
        sort: this.itemTraitSet.sort,
        search_term: this.itemTraitSet.searchTerm,
        only_watching: this.itemTraitSet.onlyWatching,
        created_within_days: this.itemTraitSet.createdWithinDays,
        last_updated_within_days: this.itemTraitSet.lastUpdatedWithinDays,
        customer_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.customerIDs,
        ),
        is_spam: this.itemTraitSet.isSpam,
      }

      try {
        // If hiding the load, don't reset total item count so there
        // is no "pulse" on the dashboard filter picker
        if (showResultsLoading) {
          this.totalItemCount = null
        }
        const response = await ConversationsAPI.get_Search(
          params.page,
          this.id.toString(),
          params.customer_ids,
          params.sort,
          params.is_spam,
          params.priority_ids,
          params.status_ids,
          params.status_type,
          params.assignee_ids,
          params.assigned_not_to_you,
          params.job_appointee_groupings_string,
          params.including_tag_ids,
          params.not_including_tag_ids,
          params.due_type,
          params.start_due_date,
          params.end_due_date,
          params.search_term,
          params.only_watching,
          params.created_within_days,
          params.last_updated_within_days,
        )

        // Store access epoch time
        this.lastAccessEpochTime = response.accessEpochTime

        // Pagination
        this.totalItemCount = response.totalConversationCount

        // Results
        // @ts-ignore
        this.results = response.listConversations.map(item =>
          this.mapResultsToTableFormat(item),
        )

        const breadCrumbItems = [] as IBreadcrumbItemMenuItem[]

        response.listConversations.forEach(t => {
          breadCrumbItems.push({
            itemID: t.id,
            entityID: this.id,
            entityName: this.name,
            entityType: EEntities.MAILBOXES,
            title: t.title,
            refNumber: t.refNumber,
          } as IBreadcrumbItemMenuItem)
        })

        BreadcrumbAssistant.cacheBreadcrumbItemMenuItems(
          this.$store,
          breadCrumbItems,
        )
      } catch (error) {
        LocalStore.clearHomeSavedQuery(this.id)
        this.$error.emit(error)
      } finally {
        this.isLoadingResults = false
      }
    },

    async fetchFilterOptionsForListing() {
      try {
        const response = await MailboxesAPI.get_FilterOptionsForListing(this.id)
        this.hydrateFilterOptionsForListing(response)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async fetchDefaultFilterOptionCounts() {
      try {
        // Swallow if timeout (or other error) occurs...
        const counts = await MailboxesAPI.get_DefaultFilterOptionCounts(this.id)
        this.updateDefaultFilterOptionCounts(counts.listDefaultFilters)
      } catch (error) {
        // Swallow
      }
    },

    hydrateFilterOptionsForListing(
      data: IMailboxesOutputFilterOptionsForListing,
    ) {
      this.hydrateFilterOptionsForListing_Base(data)
      this.customFilterNavItems = data.listCustomFilters.map(c => ({
        value: ItemFilterAssistant.getCustomFilterIdentifier(c.id),
        label: c.name,
        icon: 'filter',
      }))
      this.customFilterItemTraitSets = data.listCustomFilters.map(c => ({
        id: c.id,
        itemTraitSet: {
          priorityIDs: c.listPriorityIDs ? c.listPriorityIDs : [],
          statusIDs: c.listStatusIDs ? c.listStatusIDs : [],
          statusType: c.statusType ? c.statusType : '',
          assigneeIDs: c.listAssigneeIDs ? c.listAssigneeIDs : [],
          assignedNotToYou: c.assignedNotToYou,
          jobAppointeeGroupingsString: ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
            c.listJobAppointeeGroupings,
          ),
          dueType: c.dueType ? c.dueType : null,
          includingTagIDs: c.listIncludingTagIDs ? c.listIncludingTagIDs : [],
          notIncludingTagIDs: c.listNotIncludingTagIDs
            ? c.listNotIncludingTagIDs
            : [],
          startDueDate: c.startDueDate
            ? moment(c.startDueDate).format('YYYY-MM-DD')
            : null,
          endDueDate: c.endDueDate
            ? moment(c.endDueDate).format('YYYY-MM-DD')
            : null,
          searchTerm: c.searchTerm ? c.searchTerm : '',
          onlyWatching: c.onlyWatching,
          createdWithinDays: c.createdWithinDays,
          lastUpdatedWithinDays: c.lastUpdatedWithinDays,
          creatorIDs: [], // Never used, default it so the comparison is equal in isItemTraitSetEquivalent()
          customerIDs: c.listCustomerIDs ? c.listCustomerIDs : [],
          isSpam: c.isSpam,
          sort: SortOptionValues.UPDATED_RECENT,
          projectIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
          mailboxIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
        } as IItemTraitSet,
      }))
    },

    async getMembers() {
      try {
        this.members = await MailboxesAPI.get_Members(this.id)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async createCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCustomerIDs: this.itemTraitSet.customerIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          isSpam: this.itemTraitSet.isSpam,
          onlyWatching: this.itemTraitSet.onlyWatching,
        } as IMailboxesInputManageCustomFilter
        const response = await MailboxesAPI.post_CreateCustomFilter(
          this.id,
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async updateCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCustomerIDs: this.itemTraitSet.customerIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          isSpam: this.itemTraitSet.isSpam,
          onlyWatching: this.itemTraitSet.onlyWatching,
        } as IMailboxesInputUpdateCustomFilter
        const response = await MailboxesAPI.put_UpdateCustomFilter(
          this.id,
          ItemFilterAssistant.getIDOfFilterIdentifier(
            this.currentFilterNavItem.value,
          ),
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async deleteCustomFilter(identifier: string) {
      try {
        const response = await CustomFiltersAPI.delete_Delete(
          ItemFilterAssistant.getIDOfFilterIdentifier(identifier),
        )

        this.fetchFilterOptionsForListing()

        if (this.currentFilterNavItem.value !== identifier) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    sendToNewConversation() {
      RouteAssistant.goToCreateConversation(
        this.$router,
        this.accountID,
        this.id,
        this.name,
      )
    },

    mapResultsToTableFormat(item: IConvosOutputSearchConvo): IHomeTableItem {
      return {
        ...item,
        parent: item.mailbox,
        entityType: EEntities.MAILBOXES,
      }
    },
  },
})
